import React, { useState } from "react";
import { DocumentScanner, Image, VideoCall } from "@mui/icons-material";
import { NOTIFICATION_KIND } from "../constants";
import { resolveNotificationKind, selectLocalizedMessage } from "../model/notificationModel";
import { handleDownloadPDF, handleGeneratePDF, handleViewPDF } from "../../../utils/generatePDF";

function MediaMessage({ icon, label }) {
  return (
    <p className="flex-1 flex justify-start text-sm text-gray-500 gap-1 items-center font-medium truncate">
      {icon}
      <span className="truncate">{label}</span>
    </p>
  );
}

function mediaLabel(notification, t, key, defaultValue) {
  const count = notification?.medias?.length ?? 0;
  const fileName = notification?.medias?.[0]?.fileName;

  if (count > 1) {
    return t("main.notifications.media.multiple", {
      count,
      defaultValue: `${count} attachments`,
    });
  }

  return fileName || t(key, { defaultValue });
}

function PdfActionButton({ label, pendingLabel, className, action, onMarkRead }) {
  const [isPending, setIsPending] = useState(false);

  const handleClick = async (event) => {
    event.stopPropagation();

    if (isPending) return;

    setIsPending(true);

    let succeeded = false;

    try {
      succeeded = await action();
    } catch {
      succeeded = false;
    } finally {
      setIsPending(false);
    }

    if (succeeded) onMarkRead?.();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isPending}
      className={`${className} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {isPending ? pendingLabel : label}
    </button>
  );
}

function SystemMessage({ notification, language }) {
  return (
    <p className="flex-1 justify-start text-sm font-medium text-gray-800 break-words">
      {selectLocalizedMessage(notification, language)}
    </p>
  );
}

function TextMessage({ notification, t }) {
  const message = String(notification.message ?? "").trim();

  if (!message) {
    return (
      <MediaMessage
        icon={<DocumentScanner />}
        label={mediaLabel(
          notification,
          t,
          "main.notifications.media.document",
          "Attachment received",
        )}
      />
    );
  }

  return (
    <p className="flex-1 justify-start text-sm font-medium truncate" title={message}>
      {message}
    </p>
  );
}

function PdfActionRequired({ notification, t, addToast, onMarkRead }) {
  return (
    <div className="flex-1 flex flex-col gap-2 py-1">
      <p className="text-sm font-medium text-gray-800">{notification.message}</p>
      <PdfActionButton
        label={t("main.reports.generatePdf")}
        pendingLabel={t("main.notifications.actions.generatingPdf", {
          defaultValue: "Generating...",
        })}
        className="self-start px-3 py-1 rounded text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white transition"
        action={() => handleGeneratePDF(notification.overviewImageId, addToast, t)}
        onMarkRead={onMarkRead}
      />
    </div>
  );
}

function PdfReady({ notification, t, addToast, language, onMarkRead }) {
  return (
    <div className="flex-1 flex flex-col gap-2 py-1">
      <p className="text-sm font-medium text-gray-800">{notification.message}</p>
      <div className="flex gap-2">
        <PdfActionButton
          label={t("main.reports.viewPdf")}
          pendingLabel={t("main.notifications.actions.openingPdf", { defaultValue: "Opening..." })}
          className="px-3 py-1 rounded text-xs font-semibold bg-red-500 hover:bg-red-600 text-white transition"
          action={() => handleViewPDF(notification.overviewImageId, addToast, t, language)}
          onMarkRead={onMarkRead}
        />
        <PdfActionButton
          label={t("main.reports.downloadPdf")}
          pendingLabel={t("main.notifications.actions.downloadingPdf", {
            defaultValue: "Downloading...",
          })}
          className="px-3 py-1 rounded text-xs font-semibold border border-red-500 text-red-500 hover:bg-red-50 transition"
          action={() => handleDownloadPDF(notification.overviewImageId, addToast, t, language)}
          onMarkRead={onMarkRead}
        />
      </div>
    </div>
  );
}

const RENDERERS = {
  [NOTIFICATION_KIND.PDF_GENERATION_REQUIRED]: PdfActionRequired,
  [NOTIFICATION_KIND.PDF_GENERATION_FAILED]: PdfActionRequired,
  [NOTIFICATION_KIND.PDF_READY]: PdfReady,
  [NOTIFICATION_KIND.ACCESS_GRANTED]: SystemMessage,
  [NOTIFICATION_KIND.ACCESS_REVOKED]: SystemMessage,
  [NOTIFICATION_KIND.TEXT]: TextMessage,
  [NOTIFICATION_KIND.IMAGE]: ({ notification, t }) => (
    <MediaMessage
      icon={<Image />}
      label={mediaLabel(notification, t, "main.notifications.media.image", "Image received")}
    />
  ),
  [NOTIFICATION_KIND.VIDEO]: ({ notification, t }) => (
    <MediaMessage
      icon={<VideoCall />}
      label={mediaLabel(notification, t, "main.notifications.media.video", "Video received")}
    />
  ),
  [NOTIFICATION_KIND.DOCUMENT]: ({ notification, t }) => (
    <MediaMessage
      icon={<DocumentScanner />}
      label={mediaLabel(notification, t, "main.notifications.media.document", "Document received")}
    />
  ),
};

export function NotificationMessage({ notification, t, addToast, language, onMarkRead }) {
  const Renderer = RENDERERS[resolveNotificationKind(notification)] ?? TextMessage;

  return (
    <Renderer
      notification={notification}
      t={t}
      addToast={addToast}
      language={language}
      onMarkRead={onMarkRead}
    />
  );
}
